// Everything the design document says is computed rather than authored: which conditions are met,
// how much of the period is covered, where the gaps are, and what outcome the evidence proposes.
import {
  BasisItem,
  Condition,
  DateRange,
  IsoDate,
  OeAssessment,
  Outcome,
  Scenario,
  scopedControlOf,
  controlOf,
} from "./model";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Parts = {year: number; month: number; day: number};

const parse = (d: IsoDate): Parts => {
  const [year, month, day] = d.split("-").map(Number);
  return {year, month, day};
};

/** Months since year zero — lets a date range be compared and iterated a month at a time. */
const monthOrdinal = (d: IsoDate): number => {
  const {year, month} = parse(d);
  return year * 12 + (month - 1);
};

const lastDayOfMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

export type DemoMonth = {ordinal: number; label: string};

export const monthsOf = (period: DateRange): DemoMonth[] => {
  const start = monthOrdinal(period.from);
  const end = monthOrdinal(period.to);
  const out: DemoMonth[] = [];
  for (let o = start; o <= end; o++) out.push({ordinal: o, label: MONTH_NAMES[o % 12]});
  return out;
};

/** Format a range the way an auditor would write it — the year appears once when both ends share it. */
export const formatRange = ({from, to}: DateRange): string => {
  const a = parse(from);
  const b = parse(to);
  const left = `${a.day} ${MONTH_NAMES[a.month - 1]}${a.year === b.year ? "" : ` ${a.year}`}`;
  return `${left} – ${b.day} ${MONTH_NAMES[b.month - 1]} ${b.year}`;
};

export type ConditionState = "met" | "not_met" | "not_concluded";

/**
 * A control condition reads the referenced control-and-scope's OE conclusion, whichever module is
 * asking. There is nothing else to read: one conclusion per control-and-scope, covering its period.
 */
export const conditionState = (condition: Condition, scenario: Scenario): ConditionState => {
  if (condition.kind === "note") return condition.met ? "met" : "not_met";
  const target = scopedControlOf(scenario, condition.control, condition.scope);
  if (!target?.oe) return "not_concluded";
  return target.oe.supportsReliance ? "met" : "not_met";
};

/**
 * A note on a planned item is a check nobody has carried out yet, so it reads as outstanding rather
 * than failed. Only a control condition can already be read against a plan — which is what makes the
 * reliance check available at planning time.
 */
export const conditionStateFor = (condition: Condition, item: BasisItem, scenario: Scenario): ConditionState =>
  item.status === "planned" && condition.kind === "note" && !condition.met
    ? "not_concluded"
    : conditionState(condition, scenario);

/**
 * An item contributes coverage only while every condition it rests on holds. A plan is drawn before
 * its conditions can all be settled, so on a planned item the bar is lower: only a condition that
 * already reads as failed sinks it.
 */
export const itemIsValid = (item: BasisItem, scenario: Scenario): boolean =>
  item.conditions.every(c => {
    const state = conditionStateFor(c, item, scenario);
    return item.status === "planned" ? state !== "not_met" : state === "met";
  });

/** What is holding up a month of the period: evidence we have, evidence we intend to get, or nothing. */
export type CoverageState = "obtained" | "planned" | "none";

export type CoverageSegment = {
  /** Indices into `monthsOf(period)`, inclusive. */
  start: number;
  end: number;
  state: CoverageState;
};

const coverageByMonth = (oe: OeAssessment, scenario: Scenario): CoverageState[] => {
  const months = monthsOf(oe.period);
  const valid = oe.basis.filter(item => itemIsValid(item, scenario));
  return months.map(m => {
    const covering = valid.filter(
      item => monthOrdinal(item.span.from) <= m.ordinal && m.ordinal <= monthOrdinal(item.span.to),
    );
    if (covering.some(item => item.status === "obtained")) return "obtained";
    return covering.length > 0 ? "planned" : "none";
  });
};

/** Runs of consecutive months in the same state, for the timeline. */
export const coverageSegments = (oe: OeAssessment, scenario: Scenario): CoverageSegment[] => {
  const states = coverageByMonth(oe, scenario);
  const out: CoverageSegment[] = [];
  for (let i = 0; i < states.length; i++) {
    const last = out[out.length - 1];
    if (last && last.state === states[i]) last.end = i;
    else out.push({start: i, end: i, state: states[i]});
  }
  return out;
};

/** The stretches `covered` rejects, as ranges the conclusion can name. */
const uncoveredRanges = (
  oe: OeAssessment,
  scenario: Scenario,
  covered: (state: CoverageState) => boolean,
): DateRange[] => {
  const months = monthsOf(oe.period);
  return coverageSegments(oe, scenario)
    .filter(s => !covered(s.state))
    .map(s => {
      const startOrd = months[s.start].ordinal;
      const endOrd = months[s.end].ordinal;
      const startYear = Math.floor(startOrd / 12);
      const startMonth = (startOrd % 12) + 1;
      const endYear = Math.floor(endOrd / 12);
      const endMonth = (endOrd % 12) + 1;
      // Clamp to the period so a partial first or last month reads correctly.
      const from = s.start === 0 ? oe.period.from : `${startYear}-${String(startMonth).padStart(2, "0")}-01`;
      const to =
        s.end === months.length - 1
          ? oe.period.to
          : `${endYear}-${String(endMonth).padStart(2, "0")}-${lastDayOfMonth(endYear, endMonth)}`;
      return {from, to};
    });
};

/** Stretches nothing accounts for, planned or otherwise — the hole in the argument. */
export const gaps = (oe: OeAssessment, scenario: Scenario): DateRange[] =>
  uncoveredRanges(oe, scenario, state => state !== "none");

/**
 * Stretches no evidence has actually been obtained for. A basis that is still entirely a plan reads
 * as the whole period, which is what stops a plan from concluding anything on its own.
 */
export const unevidenced = (oe: OeAssessment, scenario: Scenario): DateRange[] =>
  uncoveredRanges(oe, scenario, state => state === "obtained");

/** How much of the plan has been carried out. Counted, never stored. */
export const planProgress = (oe: OeAssessment): {obtained: number; total: number} => ({
  obtained: oe.basis.filter(item => item.status === "obtained").length,
  total: oe.basis.length,
});

export type EvidenceTotals = {itemsTested: number; exceptions: number; exceedsTolerable: boolean};

export const evidenceTotals = (oe: OeAssessment, scenario: Scenario): EvidenceTotals => {
  let itemsTested = 0;
  let exceptions = 0;
  let exceedsTolerable = false;
  for (const item of oe.basis) {
    if (!itemIsValid(item, scenario)) continue;
    if (item.status !== "obtained") continue;
    if (item.evidence.kind !== "test") continue;
    itemsTested += item.evidence.itemsTested;
    exceptions += item.evidence.exceptions;
    const {deviationRate, tolerableRate} = item.evidence;
    if (deviationRate != null && tolerableRate != null && deviationRate > tolerableRate) exceedsTolerable = true;
  }
  return {itemsTested, exceptions, exceedsTolerable};
};

const outcomeFrom = (uncovered: DateRange[], totals: EvidenceTotals): Outcome => {
  // An unaccounted-for stretch wins over everything else: there is nothing to conclude from.
  if (uncovered.length > 0) return "insufficient_evidence";
  if (totals.exceedsTolerable) return "did_not_operate_effectively";
  return totals.exceptions > 0 ? "operated_with_exceptions" : "operated_effectively";
};

/**
 * The outcome the evidence points to. The auditor can still pick something else; the justification
 * field is where they say why.
 */
export const proposedOutcome = (oe: OeAssessment, scenario: Scenario): Outcome =>
  outcomeFrom(unevidenced(oe, scenario), evidenceTotals(oe, scenario));

/**
 * What the basis would conclude if every planned item came back clean. Read at planning time, when
 * there is nothing else to judge a plan against.
 */
export const projectedOutcome = (oe: OeAssessment, scenario: Scenario): Outcome =>
  outcomeFrom(gaps(oe, scenario), evidenceTotals(oe, scenario));

export type DependencyRow = {
  ref: string;
  title: string;
  assessment: string;
  automated: boolean;
  scope: string;
  outcome?: Outcome;
  supportsReliance?: boolean;
  provenance: string;
};

/**
 * Resolve an edge to the scopes of `other` that matter here: the one sharing this scope's name, or —
 * when the two controls aren't scoped alike — every scope it has, so nothing is hidden.
 */
const rowsFor = (
  scenario: Scenario,
  otherRef: string,
  scope: string,
  provenance: string,
  declaredScope?: string,
): DependencyRow[] => {
  const other = controlOf(scenario, otherRef);
  if (!other) return [];
  // A declared scope wins: it is the application the dependency was recorded against.
  const named = declaredScope ? other.scopes.filter(s => s.scope === declaredScope) : [];
  const matching = named.length > 0 ? named : other.scopes.filter(s => s.scope === scope);
  const chosen = matching.length > 0 ? matching : other.scopes;
  return chosen.map(s => ({
    ref: other.ref,
    title: other.title,
    assessment: other.assessment,
    automated: other.automated ?? false,
    scope: s.scope,
    outcome: s.oe?.outcome,
    supportsReliance: s.oe?.supportsReliance,
    provenance,
  }));
};

/** Controls this one sits on top of. */
export const upstreamRows = (scenario: Scenario, control: string, scope: string): DependencyRow[] =>
  scenario.edges.filter(e => e.to === control).flatMap(e => rowsFor(scenario, e.from, scope, e.provenance, e.scope));

/** Controls that sit on top of this one — what breaks if it fails. */
export const downstreamRows = (scenario: Scenario, control: string, scope: string): DependencyRow[] =>
  scenario.edges.filter(e => e.from === control).flatMap(e => rowsFor(scenario, e.to, scope, e.provenance, e.scope));
